// Read-only detail view for an insurance / licence / accreditation item.
// Rendered inside TrackingTable's expanded-row slot when the user clicks
// a row (view mode) — distinct from InsuranceInlineExpand which is the
// edit form. Also reused by InsuranceDetailModal (archive deep-link).
//
// Renders through the shared TrackingDetailPanel: the derived status pill
// + historical renewal reference compose into the panel's badges slot, the
// record fields into its <dl>, and the evidence links + notes sections
// into its children slot. Evidence links render as real clickable anchors
// — the whole point of a detail view is that they're usable, not displayed
// behind disabled inputs.

import { useMemo } from "react";
import { Pencil, ExternalLink, AlertTriangle } from "lucide-react";
import Button from "../../../common/Button";
import TrackingStatusBadge from "../shared/TrackingStatusBadge";
import TrackingDetailPanel, {
  type TrackingDetailField,
} from "../shared/TrackingDetailPanel";
import CopyableIdentifier from "../../../common/CopyableIdentifier";
import ReminderSchedulePills from "./ReminderSchedulePills";
import RenewalReference from "./RenewalReference";
import type { HistoricalSuccessor } from "../../../../hooks/useInsuranceHistorical";
import { safeExternalHref } from "../../../../utils/evidenceLinks";
import type {
  Insurance,
  InsuranceStatus,
  InsuranceType,
} from "../../../../types";
import {
  INSURANCE_CATEGORY_LABELS,
  INSURANCE_TYPE_LABELS,
} from "../../../../types";
import { getBrisbaneToday } from "../../../../utils/getBrisbaneToday";
import { formatAud0dp } from "../../../../utils/formatCurrency";

interface InsuranceDetailExpandProps {
  item: Insurance;
  /** Called when the user clicks the Edit button in the detail header.
   *  Parent swaps expandedMode to 'edit'. Omitted on archived items
   *  (where editing is gated by an unarchive flow). */
  onEdit?: () => void;
  /** Status derived from startDate + expiryDate. Pre-computed by the
   *  parent so derivation logic stays in one place (InsuranceTab). */
  derivedStatus?: InsuranceStatus;
  /** True when this row has a qualifying active successor (renewed). Drives
   *  the muted "Expired" / "Renewed" badge and the inline RenewalReference. */
  superseded?: boolean;
  /** Successor reference supplied alongside `superseded`. The detail view
   *  renders a cross-link to this item so admins can jump from the audit-only
   *  original to the in-force renewal. */
  successor?: HistoricalSuccessor;
  /** Anchor date for the reminder pills. Defaults to Brisbane-now. */
  today?: Date;
}

function fmtDate(v?: string): string {
  if (!v) return "—";
  try {
    return new Date(`${v}T00:00:00`).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return v;
  }
}

export default function InsuranceDetailExpand({
  item,
  onEdit,
  derivedStatus,
  superseded,
  successor,
  today,
}: InsuranceDetailExpandProps) {
  const isInsurance = item.category === "insurance";
  const subjectLabel =
    item.category === "licence"
      ? "Licence name"
      : item.category === "accreditation"
        ? "Accreditation name"
        : "Subject";
  const policyLabel = isInsurance ? "Policy number" : "Reference number";
  const belowMinimum =
    isInsurance &&
    typeof item.coverageValue === "number" &&
    typeof item.minimumLimit === "number" &&
    item.coverageValue < item.minimumLimit;
  const todayDate = useMemo<Date>(() => {
    if (today) return today;
    const iso = getBrisbaneToday();
    return new Date(`${iso}T00:00:00Z`);
  }, [today]);

  const fields: TrackingDetailField[] = [
    { label: "Type", value: INSURANCE_CATEGORY_LABELS[item.category] },
    isInsurance
      ? {
          label: "Insurance types",
          value:
            item.insuranceTypes && item.insuranceTypes.length > 0
              ? item.insuranceTypes
                  .map((t: InsuranceType) => INSURANCE_TYPE_LABELS[t] ?? t)
                  .join(", ")
              : "—",
        }
      : { label: subjectLabel, value: item.subjectName ?? "—" },
    { label: "Provider", value: item.provider },
    {
      label: policyLabel,
      value: item.policyNumber ? (
        <CopyableIdentifier value={item.policyNumber} label={policyLabel} />
      ) : (
        "—"
      ),
    },
  ];
  if (isInsurance) {
    fields.push({
      label: "Coverage value",
      value: (
        <>
          {typeof item.coverageValue === "number"
            ? formatAud0dp(item.coverageValue)
            : "—"}
          {belowMinimum && typeof item.minimumLimit === "number" && (
            <div className="mt-1 inline-flex items-center gap-1 text-caption text-amber-600 dark:text-amber-500">
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
              required minimum: {formatAud0dp(item.minimumLimit)}
            </div>
          )}
        </>
      ),
    });
  }
  if (isInsurance && typeof item.minimumLimit === "number") {
    fields.push({
      label: "Minimum limit",
      value: formatAud0dp(item.minimumLimit),
    });
  }
  if (item.contractClause) {
    fields.push({ label: "Contract clause", value: item.contractClause });
  }
  fields.push(
    { label: "Start date", value: fmtDate(item.startDate) },
    { label: "Expiry date", value: fmtDate(item.expiryDate) },
    {
      label: "Reminder days",
      value:
        item.reminderDays && item.reminderDays.length > 0 ? (
          <ReminderSchedulePills
            today={todayDate}
            startDate={item.startDate}
            expiryDate={item.expiryDate}
            reminderDays={item.reminderDays}
          />
        ) : (
          "—"
        ),
    },
  );

  return (
    <TrackingDetailPanel
      testId="insurance-detail-expand"
      title={item.name}
      badges={
        <>
          {derivedStatus && (
            <TrackingStatusBadge
              kind="insurance"
              status={derivedStatus}
              superseded={superseded}
              successorName={successor?.name}
            />
          )}
          {superseded && successor && (
            <RenewalReference
              successorId={successor.id}
              successorName={successor.name}
            />
          )}
        </>
      }
      fields={fields}
      actions={
        onEdit ? (
          <Button
            variant="secondary"
            size="sm"
            icon={<Pencil className="h-4 w-4" />}
            onClick={onEdit}
          >
            Edit
          </Button>
        ) : undefined
      }
    >
      {item.evidenceLinks && item.evidenceLinks.length > 0 && (
        <div>
          <h4 className="text-caption text-slate-500 dark:text-slate-400">
            Evidence links
          </h4>
          <ul className="mt-2 space-y-1">
            {item.evidenceLinks.map((link, i) => {
              const href = safeExternalHref(link.url);
              const text = link.label || link.url;
              // Non-http(s) URLs (incl. already-stored javascript:/data: payloads)
              // render as plain text — never a clickable anchor.
              return (
                <li key={i}>
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-body text-brand-600 hover:text-brand-500 dark:text-brand-400 hover:underline"
                    >
                      {text}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  ) : (
                    <span className="text-body text-slate-700 dark:text-slate-300">
                      {text}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {item.notes && (
        <div>
          <h4 className="text-caption text-slate-500 dark:text-slate-400">
            Notes
          </h4>
          <p className="mt-1 text-body whitespace-pre-wrap text-slate-900 dark:text-slate-100">
            {item.notes}
          </p>
        </div>
      )}
    </TrackingDetailPanel>
  );
}
